import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44, supabase } from '@/api/base44Client';

const DEFAULTS = {
  bca: { nama_bank: 'BCA', name: 'Apa Saja Bisa', label: 'Saldo Operasional', number: '1741359516', startBalance: 8653969 },
  bni: { nama_bank: 'BCA', name: 'Satala Dermatech Essential', label: 'Saldo Utama', number: '6821535384', startBalance: 8411293 },
  mandiri: { nama_bank: 'BCA', name: 'Cuan Jalan Terus', label: 'Saldo Operasional', number: '1741359958', startBalance: -850704 }
};

export function useBankAccounts() {
  const qc = useQueryClient();

  // Fetch CompanySettings (single record that already exists in Supabase)
  const { data: settingsList = [], isLoading } = useQuery({
    queryKey: ['company-settings-bank-accounts'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.from('CompanySettings').select('*').limit(1);
        if (!error && data && data.length > 0) {
          return [{ ...data[0].data, id: data[0].id, created_date: data[0].created_date, updated_date: data[0].updated_date }];
        }
      } catch (e) {
        console.warn('Direct supabase fetch failed, falling back to base44:', e);
      }
      return base44.entities.CompanySettings.list('-updated_date');
    },
    staleTime: 30000,
  });

  const settings = settingsList[0] || {};
  const fallbackEmpty = React.useMemo(() => ({}), []);
  const bankAccounts = settings.bank_accounts || fallbackEmpty;

  // Build accountsData array from the stored JSON
  const accountsData = React.useMemo(() => Object.values(bankAccounts), [bankAccounts]);

  // Build semantic map with defaults overlaid by DB data
  const accountsMap = React.useMemo(() => {
    const map = {};
    if (Object.keys(bankAccounts).length === 0) {
      Object.assign(map, DEFAULTS);
    } else {
      Object.keys(bankAccounts).forEach(key => {
        map[key] = { ...(DEFAULTS[key] || {}), ...bankAccounts[key] };
      });
    }
    return map;
  }, [bankAccounts]);

  // Save bank_accounts back to CompanySettings
  const saveMut = useMutation({
    mutationFn: async (newBankAccounts) => {
      if (settings.id) {
        return base44.entities.CompanySettings.update(settings.id, { bank_accounts: newBankAccounts });
      } else {
        return base44.entities.CompanySettings.create({ bank_accounts: newBankAccounts });
      }
    },
    onMutate: async (newBankAccounts) => {
      await qc.cancelQueries({ queryKey: ['company-settings-bank-accounts'] });
      const prev = qc.getQueryData(['company-settings-bank-accounts']);
      qc.setQueryData(['company-settings-bank-accounts'], (old = []) => {
        if (old.length > 0) {
          return [{ ...old[0], bank_accounts: newBankAccounts, updated_date: new Date().toISOString() }];
        }
        return [{ bank_accounts: newBankAccounts, id: 'temp_' + Date.now(), created_date: new Date().toISOString(), updated_date: new Date().toISOString() }];
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['company-settings-bank-accounts'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['company-settings-bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['company-settings'] });
    }
  });

  const _updateAccount = async (key, data) => {
    const updated = { ...bankAccounts, [key]: { ...accountsMap[key], nama_bank: key, ...data } };
    await saveMut.mutateAsync(updated);
  };

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ['company-settings-bank-accounts'] });
    qc.invalidateQueries({ queryKey: ['company-settings'] });
  };

  return {
    accountsData,
    accountsMap,
    isLoading,
    refetch,
    createAccount: (data) => {
      const key = data.nama_bank || 'bca';
      return _updateAccount(key, data);
    },
    updateAccount: (id, data) => {
      const isSystemKey = Object.keys(accountsMap).includes(id);
      const key = isSystemKey ? id : (Object.keys(accountsMap).find(k => accountsMap[k]?.id === id) || id);
      return _updateAccount(key, data);
    },
    deleteAccount: async (key) => {
      const updated = { ...bankAccounts };
      delete updated[key];
      await saveMut.mutateAsync(updated);
    },
  };
}
